import { Autocomplete, Button, type Item, TextField } from '@ads/components-react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getAutocompleteSearchMessage } from '@/shared/utils/getAutocompleteSearchMessage';

import { useCompaniesDropdown } from '../../../hooks/useCompaniesDropdown';
import { usePersonFilters } from '../../../hooks/usePersonList';
import { useStudentSearchDropdown } from '../../../hooks/useStudentSearchDropdown';

export function PersonFilterBar() {
  const { t } = useTranslation('personManagement');
  const { t: tCommon } = useTranslation();
  const { filters, updateFilters, resetFilters } = usePersonFilters();

  const hasAppliedFilters = !!filters.name || !!filters.cpf || filters.companyId !== null;

  const [pendingName, setPendingName] = useState(filters.name);
  const [pendingEmail, setPendingEmail] = useState(filters.email);
  const [pendingCpf, setPendingCpf] = useState(filters.cpf);
  const [pendingCompany, setPendingCompany] = useState<Item<string> | null>(null);
  const [personSearch, setPersonSearch] = useState('');
  const [companySearch, setCompanySearch] = useState('');

  const { students: personResults, isQuerySuccess: isPersonQuerySuccess } =
    useStudentSearchDropdown(personSearch);
  const { companies, isQuerySuccess: isCompanyQuerySuccess } = useCompaniesDropdown(companySearch);

  const personItems = personResults.map(
    (p): Item<string> => ({ label: p.value ?? '', value: p.value ?? '' }),
  );
  const personDisplayLabel = pendingName
    ? pendingEmail
      ? `${pendingName} [${pendingEmail}]`
      : pendingName
    : '';
  const selectedPersonItem: Item<string> | undefined = personDisplayLabel
    ? { label: personDisplayLabel, value: personDisplayLabel }
    : undefined;
  const personMessage = getAutocompleteSearchMessage(
    personSearch.length,
    isPersonQuerySuccess,
    personItems.length,
    {
      minChars: tCommon('search.minChars'),
      searching: tCommon('search.searching'),
      notFound: tCommon('search.notFound'),
    },
  );

  const companyItems = companies.map((c): Item<string> => ({ label: c.name, value: c.id }));
  const companyMessage = getAutocompleteSearchMessage(
    companySearch.length,
    isCompanyQuerySuccess,
    companyItems.length,
    {
      minChars: tCommon('search.minChars'),
      searching: tCommon('search.searching'),
      notFound: tCommon('search.notFound'),
    },
  );

  const handleSearch = () => {
    updateFilters({
      name: pendingName,
      email: pendingEmail,
      cpf: pendingCpf,
      companyId: pendingCompany ? parseInt(pendingCompany.value ?? '', 10) : null,
      page: 1,
    });
  };

  const handleClear = () => {
    setPendingName('');
    setPendingEmail('');
    setPendingCpf('');
    setPendingCompany(null);
    setPersonSearch('');
    setCompanySearch('');
    resetFilters();
  };

  return (
    <div className="flex flex-col">
      {/* Row 1: busca por pessoa + botão pesquisar */}
      <div className="flex items-end justify-between gap-nano px-xs pt-xs pb-mini">
        <Autocomplete<string>
          label={t('personList.filters.person')}
          placeholder={t('personList.filters.personPlaceholder')}
          size="md"
          list={personItems}
          custom={{ root: 'w-full', groundedWrapper: 'w-full' }}
          {...(personMessage !== undefined ? { notFoundMessage: personMessage } : {})}
          selectedItem={selectedPersonItem ?? { label: '', value: '' }}
          onChange={e => {
            setPersonSearch(e.target.value);
          }}
          handleChange={item => {
            const display = item.value ?? '';
            const emailMatch = display.match(/\[(.+)\]$/);
            const extractedEmail = emailMatch?.[1] ?? '';
            const extractedName = emailMatch
              ? display.slice(0, display.lastIndexOf(' [')).trim()
              : display.trim();
            setPendingName(extractedName);
            setPendingEmail(extractedEmail);
            setPersonSearch('');
          }}
          handleClearQuery={() => {
            setPendingName('');
            setPendingEmail('');
            setPersonSearch('');
          }}
        />

        <Button
          hierarchy="primary"
          size="md"
          leadingIcon={MagnifyingGlassIcon}
          onClick={handleSearch}
        >
          {t('personList.filters.search')}
        </Button>
      </div>

      {/* Row 2: empresa + CPF + limpar */}
      <div className="flex flex-wrap items-end gap-nano px-xs pb-xs">
        <Autocomplete<string>
          label={t('personList.filters.company')}
          placeholder={t('personList.filters.companyPlaceholder')}
          size="md"
          list={companyItems}
          {...(companyMessage !== undefined ? { notFoundMessage: companyMessage } : {})}
          selectedItem={pendingCompany ?? { label: '', value: '' }}
          onChange={e => setCompanySearch(e.target.value)}
          handleChange={item => {
            setPendingCompany(item);
            setCompanySearch('');
          }}
          handleClearQuery={() => {
            setPendingCompany(null);
            setCompanySearch('');
          }}
        />

        <TextField
          label={t('personList.filters.cpf')}
          placeholder={t('personList.filters.cpfPlaceholder')}
          value={pendingCpf}
          size="md"
          onChange={e => setPendingCpf(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') handleSearch();
          }}
        />

        {hasAppliedFilters && (
          <button
            type="button"
            onClick={handleClear}
            className="inline-flex items-center gap-quark rounded-pill border-hairline border-ctx-layout-border bg-ctx-layout-body px-xxs py-quark ts-caption-xxs text-ctx-content-placeholder transition-colors hover:bg-ctx-interactive-secondary hover:text-ctx-content-base"
          >
            <XMarkIcon className="h-[0.8125rem] w-[0.8125rem]" />
            {t('personList.filters.clear')}
          </button>
        )}
      </div>
    </div>
  );
}
